import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
    FileSignature, Plus, X, Download, 
    CheckCircle2, XCircle, AlertTriangle, 
    Calendar, MapPin, Search, ChevronLeft,
    Building2, ClipboardList
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const ContractorPermitList = () => {
    const [view, setView] = useState('list'); 
    const [permits, setPermits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Form Data
    const [projects, setProjects] = useState([]);
    const [availablePermits, setAvailablePermits] = useState([]);
    const [permitData, setPermitData] = useState({}); 
    
    const [formData, setFormData] = useState({ 
        project: '', 
        date: new Date().toISOString().split('T')[0], 
        location: '' 
    });

    useEffect(() => { 
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        loadPermits(); 
        loadConfig(); 
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadPermits = async () => {
        try { const res = await api.get('approvals/mine/'); setPermits(res.data); setLoading(false); } 
        catch (err) { console.error(err); setLoading(false); }
    };
    const loadConfig = async () => {
        try { const [pRes, tRes] = await Promise.all([api.get('config/projects/'), api.get('config/permits/')]); setProjects(pRes.data); setAvailablePermits(tRes.data); } 
        catch (err) { console.error(err); }
    };

    const handleDownloadPDF = async (permitId) => {
        try {
            const response = await api.get(`permits/${permitId}/pdf/`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Permit_${permitId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) { 
            console.error("PDF Error", err);
            alert("Error downloading PDF"); 
        }
    };

    // --- FORM HANDLERS ---
    const handleTogglePermit = (id) => {
        setPermitData(prev => {
            const newData = { ...prev };
            if (newData[id]) delete newData[id]; 
            else newData[id] = { notes: '', answers: {} }; 
            return newData;
        });
    };

    const handleAnswerChange = (pId, qIdx, value) => {
        setPermitData(prev => ({
            ...prev, [pId]: { ...prev[pId], answers: { ...prev[pId].answers, [qIdx]: value } }
        }));
    };

    const handleNotesChange = (pId, text) => {
        setPermitData(prev => ({
            ...prev, [pId]: { ...prev[pId], notes: text }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.project) return alert("Please select a Project.");
        if (!formData.date) return alert("Please select a Date.");
        if (Object.keys(permitData).length === 0) return alert("Please select at least one permit type.");

        const requests = Object.keys(permitData).map(async (permitId) => {
            const data = permitData[permitId];
            const type = availablePermits.find(p => p.id === parseInt(permitId));
            
            let desc = "--- CHECKLIST ---\n";
            if (type && type.questions) {
                type.questions.forEach((q, idx) => {
                    const qText = typeof q === 'object' ? q.text : q;
                    const ansValue = data.answers[idx];
                    
                    // Format answer for display
                    let displayAns = "NO";
                    if (ansValue === true || ansValue === 'Yes') displayAns = "YES";
                    else if (ansValue === false || ansValue === 'No') displayAns = "NO";
                    else if (ansValue && typeof ansValue === 'string') displayAns = ansValue; 
                    
                    desc += `${qText}: ${displayAns}\n`;
                });
            }
            if (data.notes) desc += `\n--- NOTES ---\n${data.notes}`;

            return api.post('permits/submit/', {
                project: formData.project, 
                date: formData.date, 
                location: formData.location, 
                type_id: parseInt(permitId), 
                details: desc
            });
        });

        try {
            await Promise.all(requests);
            alert("Permits Submitted Successfully!");
            setView('list'); 
            setPermitData({}); 
            setFormData(p => ({...p, location: ''})); 
            loadPermits();
        } catch (err) { 
            console.error("Submit Error:", err);
            const msg = err.response?.data?.detail || err.response?.data?.message || "Error submitting permits.";
            alert(`Error: ${msg}`); 
        }
    };

    // --- RENDER HELPERS ---
    const RadioPill = ({ label, selected, onClick, color }) => (
        <div onClick={onClick} style={{
            flex: 1, textAlign: 'center', padding: '10px 5px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, borderRadius: '6px', 
            border: `1px solid ${selected ? color : THEME.border}`, 
            background: selected ? `${color}33` : 'transparent', 
            color: selected ? color : THEME.textSec, 
            transition: 'all 0.2s', userSelect: 'none'
        }}>
            {label}
        </div>
    );

    const renderQuestions = (type) => {
        if (!type.questions) return null;

        return type.questions.map((q, idx) => {
            const qText = typeof q === 'object' ? q.text : q;
            const qType = typeof q === 'object' ? (q.type || 'checkbox') : 'checkbox';
            
            // Logic Jump Checks
            const showIfId = typeof q === 'object' ? q.showIf : null;
            const showIfCond = (typeof q === 'object' ? q.showIfCondition : 'Yes') || 'Yes'; 

            if (showIfId) {
                const parentIdx = type.questions.findIndex(pq => (typeof pq === 'object' ? pq.id : null) === showIfId);
                const parentAnswer = permitData[type.id]?.answers?.[parentIdx];

                const normalizedAnswer = (parentAnswer === true) ? 'Yes' : (parentAnswer === false ? 'No' : parentAnswer);
                
                if (normalizedAnswer !== showIfCond) return null;
            }

            return (
                <div key={idx} style={{marginBottom:'15px', paddingLeft: showIfId ? '15px' : '0', borderLeft: showIfId ? `2px solid ${THEME.border}` : 'none'}}>
                    <label style={{display:'block', color:THEME.textSec, fontSize:'0.85rem', marginBottom:'6px'}}>{qText}</label>
                    {qType === 'text' ? (
                        <input 
                            type="text"
                            value={permitData[type.id]?.answers?.[idx] || ''}
                            onChange={(e) => handleAnswerChange(type.id, idx, e.target.value)}
                            style={inputStyle}
                            placeholder="Enter details..."
                        />
                    ) : (
                        <div style={{display:'flex', gap:'10px'}}>
                            {['Yes', 'No'].map(opt => (
                                <RadioPill 
                                    key={opt} 
                                    label={opt} 
                                    selected={permitData[type.id]?.answers?.[idx] === opt} 
                                    onClick={() => handleAnswerChange(type.id, idx, opt)}
                                    color={opt === 'Yes' ? THEME.success : THEME.danger}
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        });
    };

    const StatusBadge = ({ status }) => {
        let color = THEME.warning;
        let icon = <AlertTriangle size={12} />;
        if (status === 'APPROVED') { color = THEME.success; icon = <CheckCircle2 size={12} />; }
        if (status === 'REJECTED') { color = THEME.danger; icon = <XCircle size={12} />; }
        return <span style={{padding:'4px 10px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold', background:`${color}22`, color:color, border:`1px solid ${color}44`, display:'flex', alignItems:'center', gap:'4px', width:'fit-content'}}>{icon} {status}</span>;
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', boxSizing:'border-box', marginBottom:'0' };

    return (
        <div className="animate-fade-in">
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <FileSignature size={28} color={THEME.primary}/>
                    <h2 style={{color:'white', margin:0}}>My Permits</h2>
                </div>
                {view === 'list' ? (
                    <button onClick={() => setView('new')} style={{padding:'10px 20px', background: THEME.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', fontWeight:'bold'}}>
                        <Plus size={18}/> Request Permit
                    </button>
                ) : (
                    <button onClick={() => setView('list')} style={{padding:'10px 20px', background: 'transparent', color: THEME.textSec, border:`1px solid ${THEME.border}`, borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px'}}>
                        <ChevronLeft size={18}/> Cancel
                    </button>
                )}
            </div>

            {view === 'list' && (
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap:'20px'}}>
                    {permits.map(p => (
                        <div key={p.id} style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, position:'relative', display:'flex', flexDirection:'column', gap:'15px'}}>
                            
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                                <div>
                                    <div style={{fontWeight:'bold', color:'white', fontSize:'1.1rem'}}>{p.permit_type_details?.name}</div>
                                    <div style={{fontSize:'0.85rem', color: THEME.primary, fontWeight:'bold', marginTop:'2px'}}>{p.display_id || `#${p.id}`}</div>
                                </div>
                                <StatusBadge status={p.status} />
                            </div>

                            <div style={{fontSize:'0.9rem', color:THEME.textSec, display:'flex', flexDirection:'column', gap:'5px'}}>
                                <span style={{display:'flex', alignItems:'center', gap:'8px'}}><Building2 size={14}/> {p.project_name}</span>
                                <span style={{display:'flex', alignItems:'center', gap:'8px'}}><MapPin size={14}/> {p.location_label}</span>
                                <span style={{display:'flex', alignItems:'center', gap:'8px'}}><Calendar size={14}/> {p.date_label}</span>
                            </div>
                            
                            <div style={{borderTop:`1px solid ${THEME.border}`, paddingTop:'15px', marginTop:'auto'}}>
                                {p.status === 'APPROVED' ? (
                                    <button onClick={() => handleDownloadPDF(p.id)} style={{width:'100%', background:THEME.primary, border:'none', color:'white', borderRadius:'6px', cursor:'pointer', padding:'10px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontWeight:'bold'}}>
                                        <Download size={16}/> Download PDF
                                    </button>
                                ) : (
                                    <div style={{textAlign:'center', fontSize:'0.85rem', color:THEME.textSec, fontStyle:'italic'}}>
                                        {p.status === 'PENDING' ? 'Waiting for approval...' : 'Application rejected.'}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {permits.length === 0 && <div style={{color:THEME.textSec, textAlign:'center', gridColumn:'1/-1', padding:'40px'}}>No permits found.</div>}
                </div>
            )}

            {view === 'new' && (
                <div className="card" style={{background: THEME.card, padding: isMobile ? '15px' : '25px', borderRadius:'12px', border:`1px solid ${THEME.border}`, maxWidth:'800px', margin:'0 auto'}}>
                    <form onSubmit={handleSubmit}>
                        <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'20px', marginBottom:'20px'}}>
                            <div>
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.85rem', marginBottom:'5px'}}>Project</label>
                                <select value={formData.project} onChange={e => setFormData({...formData, project: e.target.value})} style={inputStyle}>
                                    <option value="">Select Project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.85rem', marginBottom:'5px'}}>Date Required</label>
                                <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={inputStyle} />
                            </div>
                        </div>
                        
                        <div style={{marginBottom:'30px'}}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.85rem', marginBottom:'5px'}}>Specific Location</label>
                            <input placeholder="e.g. Roof Section B, Boiler Room..." value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} style={inputStyle} />
                        </div>
                        
                        <h4 style={{color:'white', marginBottom:'15px', display:'flex', alignItems:'center', gap:'10px'}}><ClipboardList size={18} color={THEME.primary}/> Select Permit Types</h4>
                        <div style={{display:'grid', gap:'15px'}}>
                            {availablePermits.map(type => {
                                const isSel = !!permitData[type.id];
                                return (
                                    <div key={type.id} style={{background: isSel ? 'rgba(59,130,246,0.1)' : '#020617', padding:'15px', borderRadius:'8px', border:`1px solid ${isSel ? THEME.primary : THEME.border}`}}>
                                        <div style={{display:'flex', alignItems:'center', cursor:'pointer'}} onClick={() => handleTogglePermit(type.id)}>
                                            <div style={{width:'20px', height:'20px', borderRadius:'4px', border:`2px solid ${isSel ? THEME.primary : THEME.textSec}`, display:'flex', alignItems:'center', justifyContent:'center', marginRight:'10px'}}>
                                                {isSel && <div style={{width:'10px', height:'10px', background: THEME.primary, borderRadius:'2px'}} />}
                                            </div>
                                            <span style={{color: isSel ? 'white' : THEME.textSec, fontWeight:'bold', fontSize:'1rem'}}>{type.name}</span>
                                        </div>
                                        
                                        {isSel && (
                                            <div style={{marginTop:'20px', paddingLeft:'15px', borderLeft:`2px solid ${THEME.primary}44`}}>
                                                {renderQuestions(type)}
                                                
                                                <div style={{marginTop:'20px'}}>
                                                    <label style={{display:'block', color:THEME.textSec, fontSize:'0.75rem', marginBottom:'5px', textTransform:'uppercase'}}>Additional Notes</label>
                                                    <textarea 
                                                        rows="2" 
                                                        placeholder="Any extra details for the safety officer..." 
                                                        value={permitData[type.id].notes || ''} 
                                                        onChange={e => handleNotesChange(type.id, e.target.value)} 
                                                        style={{...inputStyle, resize:'vertical'}} 
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <button type="submit" style={{width:'100%', padding:'15px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', fontSize:'1rem', cursor:'pointer', marginTop:'30px', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px'}}>
                            <CheckCircle2 size={20}/> Submit Application
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ContractorPermitList;